import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { matchDevice, matchedTableQuery, type MatchedRow } from '../api/opteemum'
import { toast } from '../ui/notify'

/**
 * Fila Match a randului desfacut din Unassigned - `Opteemum::matchedDevices`
 * (`unassigned/matchedDevices.php`) cu tabelul `DeviceMatchModel` din
 * opteemum.js: aparatele firmei cu acelasi nume si tip ca cel neatribuit, si
 * pe fiecare rand butonul „Match" (`form.matchForm` spre `opteemum/matchDevice`).
 * Dupa imbinare portalul redesena `#unnasignedDevices` - aici lista se
 * reincarca prin `onMatched`.
 */

const COLUMNS = ['Type', 'Device name', 'Last User Logged On', 'Office', 'Last activity', 'Actions']

function fromNow(value: string | null): string {
  if (!value) return ''
  const then = new Date(value.replace(' ', 'T')).getTime()
  if (Number.isNaN(then)) return value
  const minutes = Math.round((Date.now() - then) / 60000)
  if (minutes < 1) return 'a few seconds ago'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  const months = Math.round(days / 30)
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`
  const years = Math.round(months / 12)
  return `${years} year${years === 1 ? '' : 's'} ago`
}

function dash() {
  return <i className="fas fa-minus font-10"></i>
}

export function UnassignedMatch({ id, onMatched }: { id: number; onMatched?: () => void }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<number | null>(null)
  const query = useQuery({
    ...matchedTableQuery(id, { start: 0, length: 100, search: '', orderColumn: 0, orderDir: 'asc' }),
    placeholderData: keepPreviousData,
  })

  const match = async (row: MatchedRow) => {
    if (busy !== null) return
    if (!window.confirm('Are you sure ?')) return
    setBusy(row.ID)
    try {
      const done = await matchDevice(id, row.ID)
      if (done.ok) {
        toast('Device matched.', 'Success', 'success')
        await queryClient.invalidateQueries({ queryKey: ['opteemum', 'unassigned'] })
        onMatched?.()
      } else toast('Something went wrong.', 'Error', 'error')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(null)
    }
  }

  const rows = query.data?.data ?? []

  return (
    <div className="row">
      <div className="col-md-12">
        <table className="table table-striped table-hover dataTable match-table" width="100%" role="grid">
          <thead>
            <tr role="row">
              {COLUMNS.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {query.isError ? (
              <tr>
                <td colSpan={COLUMNS.length} className="dataTables_empty">
                  {query.error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="dataTables_empty">
                  {query.isPending ? 'Loading...' : 'No data available in table'}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.DT_RowId} id={row.DT_RowId} role="row">
                  <td>
                    <center
                      style={{ cursor: 'default' }}
                      data-toggle="tooltip"
                      data-placement="top"
                      title={row.device_type}
                      dangerouslySetInnerHTML={{ __html: row.device_icon }}
                    />
                  </td>
                  <td>
                    {row.Denumire}
                    {row.ownerId > 0 ? (
                      <>
                        <br />
                        <span className="color-light-grey">
                          <i className="fa fa-user"></i> {row.Prenume} {row.Nume}
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td>{row.UserLogat ? row.UserLogat : dash()}</td>
                  <td>{row.office ? row.office : dash()}</td>
                  <td>{row.DataAccesareDM ? fromNow(row.DataAccesareDM) : dash()}</td>
                  <td>
                    <form
                      className="matchForm"
                      method="post"
                      onSubmit={(e) => {
                        e.preventDefault()
                        void match(row)
                      }}
                    >
                      <input type="hidden" value={row.ID} name="matchedDevice" />
                      <input type="hidden" value={row.oldDevice} name="replacedDevice" />
                      <button className="btn btn-primary btn-xs" type="submit" disabled={busy !== null}>
                        <i className="fa fa-share-alt"></i> Match
                      </button>
                    </form>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
